import type { PrincipalCalendarEvent, ProgramListEvent } from 'models';
import { type CalendarEvent, EventCalendar } from 'ui';
import { getBrowserTimezone } from 'common/utils';
import { useCalendarSettings } from '../../../../storages';
import type { CalendarSearchParamsOptions, Layout, Mode } from '../../../../types';
import { Agenda } from '../agenda';
import { Event } from '../event';
import { useGetWeekStartsOn } from '../hooks';
import { TimeSlotDropdown } from '../time-slot-dropdown';

export type ContentEvent = NodeCalendarEvent | RailsCalendarEvent;

type NodeCalendarEvent = {
  data: PrincipalCalendarEvent;
  type: 'node';
} & CalendarEvent;

type P = {
  date: Date;
  events?: ContentEvent[];
  getPathToEventDetail: (id: string) => string;
  isLoading: boolean;
  layout: Layout;
  mode: Mode;
  navigateToEventDetail: (id: string) => Promise<void>;
  updateSearchParams: CalendarSearchParamsOptions['updateSearchParams'];
};

type RailsCalendarEvent = {
  data: ProgramListEvent;
  type: 'rails';
} & CalendarEvent;

const CalendarLayoutContent = ({
  date,
  events,
  getPathToEventDetail,
  isLoading,
  layout,
  mode,
  navigateToEventDetail,
  updateSearchParams,
}: P) => {
  const [, setCalendarSettings] = useCalendarSettings();
  const getWeekStartsOn = useGetWeekStartsOn();

  switch (layout) {
    case 'agenda':
      return (
        <Agenda
          date={date}
          events={events}
          getPathToEventDetail={getPathToEventDetail}
          isLoading={isLoading}
          mode={mode}
        />
      );
    case 'calendar':
      return (
        <EventCalendar<ContentEvent>
          date={date}
          events={events}
          isLoading={isLoading || !events}
          mode={mode}
          onMoreEventsClick={(newDate) => {
            updateSearchParams({ date: newDate });
            setCalendarSettings({ mode: 'day' });
          }}
          renderEvent={({ children, event: { data, type } }) => (
            <Event
              eventId={data.id}
              getPathToEventDetail={getPathToEventDetail}
              navigateToEventDetail={navigateToEventDetail}
              type={type}
            >
              {children}
            </Event>
          )}
          renderTimeSlot={({ children, dropdownProps, slotStart }) => (
            <TimeSlotDropdown
              dropdownProps={dropdownProps}
              slotStart={{
                date: slotStart,
                isTimeSpecific: mode !== 'month',
              }}
            >
              {children}
            </TimeSlotDropdown>
          )}
          timezones={[getBrowserTimezone()]}
          weekStartsOn={getWeekStartsOn}
        />
      );
  }
};

export default CalendarLayoutContent;
